
/**
 * Composition function for providing Url functionality
 * Note, the profile, and context params must be the actual object not a wrapped ref() object.
 */
export default function useUrls(context, request) {

  let req = request
  if ( !req ) {
    try {
      const app = useNuxtApp()
      req = app.ssrContext ? app.ssrContext.event.node.req : null      
    }
    catch(error) {
      console.log(`Error occured while trying to reference useNuxtApp composition function. This is probably
      because you are running outside of the nuxt context - maybe in a piece of middleware?  Try passing the request
      as part of useUrls.`)
    }
  }

  /**
   * Create a locationDetailUrl
   * locationId - The locationId
   */
    const locationDetailUrl = (locationId, betterChoiceContext) => {
      let url
      if ( betterChoiceContext ) {
        url = fns.buildUrl('/fcp/location-detail/', { locationId: locationId, betterChoiceContext: betterChoiceContext ? betterChoiceContext : '' })
      }
      else {
        url = fns.buildUrl('/fcp/location-detail/', { locationId: locationId })        
      }
      return url.toString()
    }
  
    /**
     * Create a practitioner-detail URL.
     * practitionerId - The practitionerId
     * locationId - The locationId
     */
    const practitionerDetailUrl = (practitionerId, locationId, betterChoiceContext) => {
      let url
      if ( betterChoiceContext ) {
        url = fns.buildUrl('/fcp/practitioner-detail/', { practitionerId: practitionerId, locationId: locationId, betterChoiceContext: betterChoiceContext ? betterChoiceContext : ''  })
      }
      else {
        url = fns.buildUrl('/fcp/practitioner-detail/', { practitionerId: practitionerId, locationId: locationId })
      }
      return url.toString()
    }

  /**
   *  Do three things here:
   *
   *  1) Build out a URL
   *  2) Append the variables in context to the URL.
   *  3) Add any user supplied URL params to the URL replacing any already existing params of the same name.
   */
  const buildUrl = function (path, params, includeContext = true) {
    const u = new URL(path, fns.baseUrl())
    //If the context attribute is a type of Array, then append a URL param for each item in the array.
    const appendMultiParams = (key, values) => {
      values.forEach((item) => {
        u.searchParams.append(key, item)
      })
    }

    if (includeContext) {
      for (let prop in context) {
        if (context[prop] != '' && context[prop] != null && context[prop] != undefined) {
          if (Array.isArray(context[prop])) appendMultiParams(prop, context[prop])
          else u.searchParams.append(prop, context[prop])
        }
      }
    }

    if (params) {
      for (let parm in params) {
        u.searchParams.set(parm, params[parm])
      }
    }

    return u
  }

  /**
   * Builds out a baseUrl.  If running on the browser, it will use browser info to determine the baseUrl.  If
   * running on the server, it will user information from the request headers to determine the baseUrl.
   */
  const baseUrl = function () {
    //If running on the browser..
    if ( typeof window != 'undefined' ) {
      const protocol = window.location.protocol

      //Note, the window.location.host seems to include the port, so no need to
      //deal with the port separately.
      const host = window.location.host
      let url = `${protocol}//${host}`

      return url
    } else {
      //const app = useNuxtApp()
      //const req = app.ssrContext ? app.ssrContext.event.node.req : null
      let protocol = req.headers['x-forwarded-proto'] ? req.headers['x-forwarded-proto'] : 'http'
      // need to trim the protocol and host bc OS4 adds en extra route for each that breaks the url
      protocol = protocol.split(',')[0].trim()
      let host = req.headers['x-forwarded-host'] ? req.headers['x-forwarded-host'] : req.headers.host
      host = host.split(',')[0].trim()
      const url = `${protocol}://${host}`
      return url
    }
  }

  /**
   * Given a javascript URL object, add the properties of an object to its URL params.
   */
     const propsToParams = (url, obj) => {
      for (const prop in obj) {
        if(obj[prop]){
          url.searchParams.set(prop, obj[prop])
        }
      }
    }

  /**
   * Returns a URL suitable for an /api call to our Nuxt /api services.  We need to make sure that we append the
   * networkId and the networkIndex if they exist, otherwise we may not get the appropriate profile built out or
   * an appropriate networkId from use-plan.  The scenarios are the following:
   *
   * 1)  Box canyons - network is determined by the networkId on the URL, and the plan is determined by the index.
   * 2)  Travel network - The network index determines the plan.  The difference here is that in 'nonauthenticated',
   *     travel networks can display more than one network - the 'regional' and the 'travel network'.  Up until
   *     travel networks, only one network was ever in plan when unauthenticated.  This is no longer true.  So we have
   *     to now consider the networkIndex even in a 'nonauthenticated' scenario.
   */
   const buildApiUrl = (urlString, params) => {
    const u = urlString.startsWith('http') ? new URL(urlString) : new URL(urlString, fns.baseUrl())
    //console.log(`Processing URL ${urlString} networkId is ${context.networkId} networkIndex is: ${context.networkIndex}`)
    if (context.networkId) u.searchParams.set('networkId', context.networkId)
    if (context.networkIndex) u.searchParams.set('networkIndex', context.networkIndex)

    if (params) {
      for (let parm in params) {
        u.searchParams.set(parm, params[parm])
      }
    }

    return u
  }    

  const fns = { baseUrl, buildUrl, buildApiUrl, propsToParams, locationDetailUrl, practitionerDetailUrl }

  return fns
}
